/**
 * UI-specific types for Application Operation Management.
 * These types mirror the SnowCommonModel structures but are decoupled for UI use.
 * Only includes fields that are actually used for form rendering.
 */

/**
 * UI-specific data type enum for attribute values.
 */
export enum UIDataType {
    /** Numeric values */
    NUMBER = 'Number',
    /** Text string values */
    STRING = 'String',
    /** Boolean true/false values */
    BOOLEAN = 'Bool',
    /** List/array of values */
    LIST = 'List',
    /** Key-value map */
    MAP = 'Map',
    /** Binary data */
    BINARY = 'Binary',
    /** Multi-line text paragraph */
    PARAGRAPH = 'Paragraph',
    /** Date value (YYYY-MM-DD) */
    DATE = 'Date',
    /** Time value (HH:MM:SS) */
    TIME = 'Time',
    /** Date range with start and end */
    DATE_RANGE = 'DateRange',
    /** Combined date and time */
    DATE_TIME = 'DateTime',
    /** JSON structured data */
    JSON = 'Json',
}

/**
 * UI-specific rendering type enum for attribute display.
 * This maps to Cloudscape components for form rendering.
 */
export enum UIRenderingType {
    /** Single-line text input field */
    TEXT = 'TEXT',
    /** Multi-line text input field for paragraphs */
    MULTILINE_TEXT = 'MULTILINE_TEXT',
    /** Single-selection dropdown */
    SELECT = 'SELECT',
    /** Multi-selection dropdown allowing multiple choices */
    MULTISELECT = 'MULTISELECT',
    /** Numeric input field */
    NUMBER = 'NUMBER',
    /** Yes/No boolean choice */
    BOOLEAN = 'BOOLEAN',
    /** Date selection (YYYY-MM-DD) */
    DATE = 'DATE',
    /** Time selection (HH:MM:SS) */
    TIME = 'TIME',
    /** Combined date and time selection */
    DATE_TIME = 'DATE_TIME',
    /** Date range selection with start and end dates */
    DATE_RANGE = 'DATE_RANGE',
    /** List/array input for multiple values */
    LIST = 'LIST',
    /** Structured JSON data editor */
    JSON = 'JSON',
    /** File/binary upload */
    BINARY = 'BINARY',
    /** Key-value pair editor for map data */
    MAP = 'MAP',
}

/**
 * UI-specific SnowAttribute interface for Application Operation Management.
 * Contains only the fields actually used for form rendering.
 * Pass-through fields (duplicate, caseHandlingType, attributeType) are excluded.
 */
export interface UISnowAttribute {
    /** Unique identifier for this attribute instance */
    attributeId: string;
    /** Data type for the attribute value */
    dataType: UIDataType;
    /** Regular expression pattern to validate the attribute's value */
    regex?: string;
    /** Human-readable name of the attribute */
    attributeName: string;
    /** Description of the attribute's purpose or usage */
    description: string;
    /** Additional help text to guide users about the attribute */
    helpText?: string;
    /** Predefined options for select/multiselect attributes */
    options?: string[];
    /** Indicates if the attribute must have a value */
    mandatory?: boolean;
    /** Rendering type in the UI for the attribute */
    renderingType?: UIRenderingType;
    /** Default value for the attribute */
    value?: string;
}
